/******************************************************************************
 *** BgtMbsdsAtrzRtso ** 운영비 * 수입/지출결의서 결재상신 리스트 조회
*******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import TextBox from "@/js/common/ui/input/TextBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import StyledTabs from "@/js/common/ui/StyledTabs";
import { Tab, TabList, TabPanel } from "react-tabs";
import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "../../common/DialogTitleBar";
import BgtAtrzTitleDialog from "./dialog/BgtAtrzTitleDialog";
import * as Atom from "@/js/common/Atom";
import { useRecoilValue } from "recoil";
import BgtMbsdsAtrzDpstList from "./BgtMbsdsAtrzDpstList";
import BgtMbsdsAtrzExpndList from "./BgtMbsdsAtrzExpndList";

const BgtMbsdsAtrzRtso = () => {
    const userInfo = useRecoilValue(Atom.userInfo);

    // 검색 조건
    const [upOgnzCd  , setUpOgnzCd  ] = React.useState<string>(userInfo.upOgnzCd);      // 상위조직코드(본부)
    const [ognzCd    , setOgnzCd    ] = React.useState<string>(userInfo.ognzCd  );      // 조직코드(지부)
    const [acntgYear , setAcntgYear ] = React.useState<string>(Util.getNowAcntgYear()); // 회계연도
    const [searchKywd, setSearchKywd] = React.useState<string>(""); // 검색어(적요)
    const [callTime, setCallTime] = React.useState<Date>(new Date()); // (UI only) 검색버튼 트리거용

    // Tabs
    const [tabIndex, setTabIndex] = React.useState<number>(0); // 0: 수입, 1: 지출
    const [procBgtSbjctSeCd, setProcBgtSbjctSeCd] = React.useState<string>(""); // 구분코드(01: 수입, 02: 지출)

    // 체크 상태 (탭별 분리)
    const [incomeCheckArr , setIncomeCheckArr ] = React.useState<any[]>([]); // 체크된 수입결의서 리스트
    const [expenseCheckArr, setExpenseCheckArr] = React.useState<any[]>([]); // 체크된 지출결의서 리스트

    // 결재 제목 입력
    const [atrzTitleModalOpen, setAtrzTitleModalOpen] = React.useState(false); // 결재 제목 입력 모달
    const [atrzTtlCn         , setAtrzTtlCn         ] = React.useState<string>(""); // 결재 제목

    const [atrzProcessing, setAtrzProcessing] = React.useState(false);  // 결재 상신 처리 중 확인 상태값

    // 결재 처리
    const doApproval = () => {
        if (atrzProcessing) return;

        AppContext.openAlert({
            title: "확인",
            msg: "결재상신을 진행하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "결재상신",
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/mbsds/atrz/process-atrz-rtso.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        procBgtSbjctSeCd: procBgtSbjctSeCd, // 구분코드(수입[01], 지출[02])
                        dataList        : procBgtSbjctSeCd === "01" ? incomeCheckArr : expenseCheckArr,
                        atrzTtlCn       : atrzTtlCn, // 결재 제목
                    },
                    onSuccessFn: (res: any) => {
                        const procCnt  = res.result.procCount; // 처리된 건수
                        const procSeNm = res.result.procSeCd === "01" ? "수입결의서" : "지출결의서"; // 적용된 구분코드명

                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "결재상신 완료",
                            msg: procCnt + "건의 " + procSeNm + " 결재상신이 완료되었습니다.",
                            icon: "check"
                        });

                        setAtrzTitleModalOpen(false); // 제목 모달 닫기
                        setAtrzProcessing(false); // 결재 버튼 활성화
                        setAtrzTtlCn(""); // 결재 제목
                        setIncomeCheckArr([]);
                        setExpenseCheckArr([]);
                        setCallTime(new Date()); // 재조회
                    },
                });
            },
            handleClose: () => {    // 취소
                setAtrzProcessing(false); // 결재버튼 활성화
            }
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">운영비 결재 상신</span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 영역 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    {/* 소속 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox value={upOgnzCd}
                            width={150} height={36}
                            fontSize={14}  fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체" paddingRight={8}
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox value={ognzCd}
                            width={150} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체" paddingRight={8}
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 회계연도 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox value={acntgYear}
                            width={120} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff"
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택" paddingRight={8}
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    {/* 적요 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">적요</span>
                        <TextBox value={searchKywd}
                            width={200} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            style={{ backgroundColor: "#fff", padding: "0 12px" }}
                            placeholder="검색어 입력"
                            onChangeFunc={(text: string) => setSearchKywd(text)}
                        />
                    </div>
                </div>
            </div>

            {/* 탭 */}
            <StyledTabs selectedIndex={tabIndex} className="mb10 tabFit">
                <TabList>
                    <Tab onClick={() => setTabIndex(0)}>수입 결의서</Tab>
                    <Tab onClick={() => setTabIndex(1)}>지출 결의서</Tab>
                </TabList>


                {/* ========================= 수입 탭 ========================= */}
                <TabPanel>
                    <BgtMbsdsAtrzDpstList
                        ognzCd     = {ognzCd}       // 조직
                        acntgYear  = {acntgYear}    // 회계연도
                        searchKywd = {searchKywd}   // 검색어(적요)
                        callTime   = {callTime}
                        incomeCheckArr        = {incomeCheckArr}    // 체크된 수입결의서 List
                        setIncomeCheckArr     = {setIncomeCheckArr}
                        setProcBgtSbjctSeCd   = {setProcBgtSbjctSeCd}   // 구분코드(수입[01])
                        setAtrzTitleModalOpen = {setAtrzTitleModalOpen} // 결재제목 모달 오픈
                        atrzProcessing        = {atrzProcessing} // 결재상신 버튼 비활성화
                    ></BgtMbsdsAtrzDpstList>
                </TabPanel>

                {/* ========================= 지출 탭 ========================= */}
                <TabPanel>
                    <BgtMbsdsAtrzExpndList
                        ognzCd     = {ognzCd}       // 조직
                        acntgYear  = {acntgYear}    // 회계연도
                        searchKywd = {searchKywd}   // 검색어(적요)
                        callTime   = {callTime}
                        expenseCheckArr       = {expenseCheckArr}      // 체크된 지출결의서 List
                        setExpenseCheckArr    = {setExpenseCheckArr}
                        setProcBgtSbjctSeCd   = {setProcBgtSbjctSeCd}   // 구분코드(지출[02])
                        setAtrzTitleModalOpen = {setAtrzTitleModalOpen}
                        atrzProcessing        = {atrzProcessing} // 결재상신 버튼 비활성화
                    ></BgtMbsdsAtrzExpndList>
                </TabPanel>
            </StyledTabs>

            {/* ========================= 결재 제목 입력 모달 ========================= */}
            <Modal modalOpen={atrzTitleModalOpen} dim={true} visible={true} titleEl={<DialogTitleBar title="결재 제목 입력" />}
                onCloseFunc={() => setAtrzTitleModalOpen(false)}
            >
                <BgtAtrzTitleDialog
                    atrzTtlCn             = {atrzTtlCn}      // 결재 제목
                    setAtrzTtlCn          = {setAtrzTtlCn}
                    setAtrzTitleModalOpen = {setAtrzTitleModalOpen}
                    doApproval            = {doApproval}   // 결재상신 처리
                    atrzProcessing        = {atrzProcessing}
                    setAtrzProcessing     = {setAtrzProcessing} // 결재상신 버튼 Disalbed
                />
            </Modal>

        </div>
    );
};

export default BgtMbsdsAtrzRtso;